// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { TypographyProps } from "@insite/mobius/Typography";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import { ellipsis, xScreen } from "@sc/XDL/Utilities";
import { mergeWithCss } from "@sc/XDL/Utilities/mergeWithCss";
import { css } from "styled-components";

interface ProductDescriptionVariantStyles {
    small?: ProductDescriptionStyles;
    medium?: ProductDescriptionStyles;
    large?: ProductDescriptionStyles;
}

const sharedStyles: { [key in keyof ProductDescriptionVariantStyles]: StyledProp } = {
    small: css`
        align-items: flex-start;
        -webkit-box-align: start;
    `,
    medium: css`
        margin-bottom: 8px;
        align-items: flex-start;
        -webkit-box-align: start;
    `,
};
const sharedSmallTextStyles: TypographyProps = {
    weight: "bold",
    color: "text.main",
    css: css`
        font-size: 14px;
        letter-spacing: 0;
        line-height: 16px;
        vertical-align: unset;
        ${wrapInContainerStyles}
        ${ellipsis(2)}
        ${xScreen("lte", "lg")} {
            ${ellipsis(3)}
        }
        ${xScreen("lte", 767)} {
            ${ellipsis(4)}
        }
    `,
};
const sharedMediumTextStyles: TypographyProps = {
    weight: "bold",
    color: "text.main",
    css: css`
        font-size: 14px;
        letter-spacing: 0;
        line-height: 16px;
        vertical-align: unset;
        ${xScreen("lte", "md")} {
            font-size: 12px;
            line-height: 13px;
        }

        ${wrapInContainerStyles}
    `,
};

const sharedLargeTextStyles: TypographyProps = {
    weight: "bold",
    color: "text.main",
    css: css`
        font-size: 28px;
        letter-spacing: 0;
        line-height: 38px;
        vertical-align: unset;
        margin-bottom: 8px;
        ${wrapInContainerStyles}
    `,
};

const productDescriptionVariantStyles: ProductDescriptionVariantStyles = {
    small: {
        productDetailDescription: {
            ...mergeWithCss(sharedSmallTextStyles, {
                css: css`
                    ${sharedStyles.small}
                    margin-bottom: 8px;
                `,
            }),
        },
        productDetailLink: {
            ...mergeWithCss(
                {
                    color: "text.main",
                    typographyProps: {
                        ...sharedSmallTextStyles,
                    },
                    css: css`
                        width: auto;
                        margin-bottom: 8px;
                    `,
                },
                { css: sharedStyles.small },
            ),
        },
    },
    medium: {
        productDetailDescription: {
            ...mergeWithCss(sharedMediumTextStyles, { css: sharedStyles.medium }),
        },
        productDetailLink: {
            ...mergeWithCss(
                {
                    color: "text.main",
                    typographyProps: {
                        ...sharedMediumTextStyles,
                    },
                    css: css`
                        width: auto;
                    `,
                },
                { css: sharedStyles.medium },
            ),
        },
    },
    large: {
        productDetailDescription: {
            ...mergeWithCss(sharedLargeTextStyles, { css: sharedStyles.large }),
        },
        productDetailLink: {
            ...mergeWithCss(
                {
                    color: "text.main",
                    typographyProps: {
                        ...sharedLargeTextStyles,
                    },
                    css: css`
                        width: auto;
                    `,
                },
                { css: sharedStyles.large },
            ),
        },
    },
};

export default productDescriptionVariantStyles;
